import React, { useState, useMemo, useEffect, useRef, Suspense  } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

import ReportExporter from "../utils/ReportExporter";
import StagingTable from "../utils/StagingTable";
import { socket } from "../utils/socket";
import FormattedNumberInput from "../utils/FormattedNumberInput";

const QuotationPDFForm = React.lazy(() =>
	import("../utils/QuotationPDFForm")
);



// Placeholders for modal fields
const placeholdersForModal = {
    customerName: "Enter Customer Name",
    item: "Enter Reminder/Reference Tag",

    region: "Enter region (e.g. Dubai)",
    total: "Enter total value (eg. 20000)",
    default: "Enter value",
};

// Labels for fields
const fieldLabels = {
    customerName: "Customer Name",
    item: "Reference Tag",
    paymentTerm: "Payment Term",
    availability: "Availability",

    total: "Total Price",
    region: "Region",
    status: "Status"
};

const QuotationsTable = () => {
    const [quotations, setQuotations] = useState([]);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [newQuotation, setNewQuotation] = useState({
        customerName: "",
        item: "",
        paymentTerm: "",
        total: "",
        region: "",
        availability: "",
        status: "",
        date: ""
    });
    
    const [ShowPDFSection, setShowPDFSection] = useState(null);

    const parentRef = useRef(null);
    const [showStaging, setShowStaging] = useState(false);
    const stagingRef = useRef(null);

    // Scroll to staging table or parent based on toggle
    useEffect(() => {
        if (showStaging && stagingRef.current) {
            stagingRef.current.scrollIntoView({ behavior: "smooth" });
        }

        if (!showStaging && parentRef?.current) {
            parentRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [showStaging]);

    // Socket: handle new quotation
    useEffect(() => {
        if (!socket) return;

        const handleNewQuotation = (data) => {
        	if (data.type === "singleNewQuotation") {
                const newQuotation = data.newQuotation;
                const mapped = {
                    id: newQuotation.id,
                    customerName: newQuotation.customer_name,
                    item: newQuotation.item_name,
                    paymentTerm: newQuotation.payment_term,

                    total: newQuotation.total,
                    region: newQuotation.region,
                    availability: newQuotation.availability,
                    status: newQuotation.status,
                    date: newQuotation.created_at
                };
                setQuotations((prev) => [mapped, ...prev]);
            }
            
            if (data.type === "quotationDeleted") {
                setQuotations(prevQuotations => prevQuotations.filter(q => q.id !== Number(data.id)));
            }

            if (data.type === "batchQuotations") { //allRows
                    
               setQuotations(prev => [...data.rows, ...prev]);
            }
        }

        socket.on("alertQuotations", handleNewQuotation);
        return () => {
            socket.off("alertQuotations", handleNewQuotation);
        };
    }, [socket]);

    const [errors, setErrors] = useState({});
    const [page, setPage] = useState(1);
    const pageSize = 8;
    const [loading, setLoading] = useState(false);

// Subscribe to dashboard store slice
const quotDat = useDashboard(state => state.data?.quotations);

// -------------------------------------
// Process mistakes requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!quotDat) return;

  try {
    // Save to state
			setQuotations(Array.isArray(quotDat) ? quotDat : []);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [quotDat]); // Reactive: runs whenever store updates

    // Validate input fields
    const validateQuotation = () => {
        const newErrors = {};
        Object.entries(newQuotation).forEach(([key, value]) => {
            if (key !== "date" && key!="total" && !value) newErrors[key] = true;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Add new quotation
    const handleAdd = async (quotation) => {
        
        if (!validateQuotation()) return;

        try {
            const res = await fetch(`${apiBaseUrl}/api/quotations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    customer_name: quotation.customerName,
                    item_name: quotation.item,
                    payment_term: quotation.paymentTerm,


                    total: Number(quotation.total) || 0,
                    region: quotation.region,
                    availability: quotation.availability,
                    status: quotation.status
                }),
            });
            if (!res.ok) throw new Error("Failed to add quotation");

            setShowModal(false);
            setNewQuotation({
                customerName: "",
                item: "",
                paymentTerm: "",

                total: "",
                region: "",
                availability: "",
                status: ""
            });
            setErrors({});
        } catch (err) {
            console.error(err);
            alert("Failed to add quotation. Try again.");
        }
    };

    // Delete quotation
    const handleDelete = async (id) => {
  		const confirmDelete = window.confirm(
  			"⚠️ Are you sure you want to delete this quotation?"
  		);
  		if (!confirmDelete) return;

        try {
            const res = await fetch(`${apiBaseUrl}/api/quotations/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete quotation");
        } catch (err) {
            console.error(err);
            alert("Failed to delete quotation. Try again.");
        }
    };

    // Filter quotations by search
    const filteredQuotations = useMemo(() => {
        const safeQuotations = Array.isArray(quotations) ? quotations : [];
        const searchLower = search.toLowerCase();
        return safeQuotations.filter(q =>
            (q.customerName?.toLowerCase() ?? "").includes(searchLower) ||
            (q.item?.toLowerCase() ?? "").includes(searchLower) ||
            (q.paymentTerm?.toLowerCase() ?? "").includes(searchLower) ||
            (q.region?.toLowerCase() ?? "").includes(searchLower) ||
            (q.availability?.toLowerCase() ?? "").includes(searchLower) ||
            (q.status?.toLowerCase() ?? "").includes(searchLower)
        );
    }, [quotations, search]);

    const totalPages = Math.ceil((filteredQuotations?.length || 0) / pageSize);
    const paginatedQuotations = useMemo(() => {
        const start = (page - 1) * pageSize;
        return (filteredQuotations || []).slice(start, start + pageSize);
    }, [filteredQuotations, page]);

    return (
        <div className="w-full  flex-1 overflow-y-auto p-4">
        
        <div className="p-6"> 
        </div>
    
            {/* Top row: Download & Used Units/Regions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
                {/* Left: Units and Regions */}
                <div className="p-2 border rounded-xl bg-white shadow-sm h-full max-w-4xl">
                    <h2 className="text-lg font-semibold text-gray-800 mb-1">
                        Used Regions
                    </h2>
                    <span className="text-xs text-gray-400 mb-3 block">
                        Please use same letters as below or add new unit.
                    </span>


                    {/* Regions */}
                    <div className="pt-4 flex flex-wrap gap-2">
                        {[...new Set(quotations.map(i => i.region))].map((type, idx) => (
                            <span
                                key={idx}
                                className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-sm shadow-sm"
                            >
                                {type}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Right: Exporter */}
                <div className="h-full">
                    <ReportExporter
                        title="Quotations Export"
                        data={quotations}
                        columns={[
                            { label: "Customer Name", field: "customerName" },
                            { label: "Reference Tag", field: "item" },
                            { label: "Payment Term", field: "paymentTerm" },


                            { label: "Total", field: "total" },
                            { label: "Region", field: "region" },
                            { label: "Availability", field: "availability" },
                            { label: "Status", field: "status" },
                            { label: "VAT", field: "vat" },
                        ]}
                        filterableFields={{
                            customerName: "Customer Name",
                            item: "Reference Tag",
                            paymentTerm: "Payment Term",
                            status: "Status",
                            availability: "availability",
                        }}
                    />
                </div>
            </div>

            {/* Search + Add Quotation + Batch Upload */}
            <div ref={parentRef} className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
                <input
                    type="text"
                    placeholder="Search by customer, item, payment term, region, availability or status..."
                    className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />

                <button
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                    onClick={() => setShowStaging(prev => !prev)}
                >
                    Batch Upload
                    <span
                        className={`ml-2 transform transition-transform duration-200 ${
                            showStaging ? "rotate-180" : "rotate-0"
                        }`}
                    >
                        ▼
                    </span>
                </button>

                <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                    onClick={() => setShowModal(true)}
                >
                    Add Quotation
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-4 text-left font-medium text-gray-700">#</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Customer Name</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Reference Tag</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Payment Term</th>

                            <th className="px-3 py-4 text-center font-medium text-gray-700">Region</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Availability</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Status</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Total</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">VAT (5%)</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Date Created</th>
                            <th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {paginatedQuotations.length === 0 && (
                            <tr>
                                <td colSpan={11} className="px-3 py-4 text-center text-gray-500 italic">
                                    {loading ? "Loading..." : "No quotations found"}
                                </td>
                            </tr>
                        )}

                        {paginatedQuotations.map((q, idx) => (
	<React.Fragment key={q.id || idx}>
		<tr className="hover:bg-gray-300 even:bg-gray-200">
			<td className="px-3 py-2">{idx + 1}</td>
			<td className="px-3 py-2 text-center">{q.customerName}</td>
			<td className="px-3 py-2 text-center">{q.item}</td>
			<td className="px-3 py-2 text-center">{q.paymentTerm}</td>


			<td className="px-3 py-2 text-center">{q.region}</td>
			<td className="px-3 py-2 text-center">{q.availability}</td>
			<td
				className={`px-3 py-2 text-center ${
					q.status === "Completed"
						? "text-green-600"
						: q.status === "Pending"
						? "text-yellow-600"
						: ""
				}`}
			>
				{q.status}
			</td>

        <td className="px-3 py-2 text-right">
          {Number(q.total || 0) === 0 ? (
            <span className="bg-yellow-600 text-white p-1 rounded">Add items</span>
          ) : (
            `${Number(q.total).toLocaleString()} AED`
          )}
        </td>

            <td className="px-3 py-2 text-center">
              {Number(q.total || 0) === 0 ? (
                <span className="bg-yellow-600 text-white p-1 rounded">Click PDF</span>
              ) : (
                `${(q.total * 0.05).toLocaleString()} AED`
              )}
            </td>

			<td className="px-3 py-2 text-center">
				{new Date(q.date).toLocaleDateString("en-GB", {
					day: "numeric",
					month: "long",
					year: "numeric",
				})}
			</td>
			<td className="px-3 py-2 text-center">
				<button
					className="px-2 py-1 mb-1 text-sm text-white bg-yellow-600 rounded-md hover:bg-yellow-700 cursor-pointer mr-1"
					onClick={() =>
						setShowPDFSection((prev) => (prev === idx ? null : idx))
					}
				>
					PDF
				</button>

				<button
					className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
					onClick={() => handleDelete(q.id)}
				>
					Delete
				</button>
			</td>
		</tr>

    		{/* This appears below the clicked row */}
        {ShowPDFSection === idx && (
        <Suspense fallback={null}>

          <QuotationPDFForm
            quotation={q}
            onClose={() => setShowPDFSection(null)}
      onQuotationUpdate={(updatedTotal) => {
        // update the total for this quotation in parent state
        setQuotations(prev =>
          prev.map(item =>
            item.id === q.id ? { ...item, total: updatedTotal } : item
          )
        );
      }}
      
                />
                </Suspense>
        )}



	</React.Fragment>
))}


                        
                        
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-5">
                    <button
                        className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
                        onClick={() => setPage(p => Math.max(p - 1, 1))}
                        disabled={page === 1}
                    >
                        Prev
                    </button>
                    <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                    <button
                        className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
                        onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}


            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
                        <h2 className="text-lg font-semibold mb-4">Add New Quotation</h2>
                        <div className="flex flex-col gap-3">
                            {Object.keys(newQuotation).map((field) => {
                                if (field === "date") return null; // Skip date in modal
                                if (field == "total") return null;

                                // Status select
                                if (field === "status") {
                                    return (
                                        <select
                                            key={field}
                                            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.status ? "border-red-500" : "border-gray-300"}`}
                                            value={newQuotation.status}
                                            onChange={(e) => setNewQuotation({ ...newQuotation, status: e.target.value })}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Completed">Completed</option>
                                        </select>
                                    );
                                }

                                // Payment term select
                                if (field === "paymentTerm") {
                                    return (
                                        <select
                                            key={field}
                                            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.paymentTerm ? "border-red-500" : "border-gray-300"}`}
                                            value={newQuotation.paymentTerm}
                                            onChange={(e) => setNewQuotation({ ...newQuotation, paymentTerm: e.target.value })}
                                        >
                                            <option value="">Select Payment Term</option>
                                            <option value="30 Days PDC">30 Days PDC</option>
                                            <option value="60 Days PDC">60 Days PDC</option>
                                            <option value="120 Days PDC">120 Days PDC</option>
                                            <option value="Cash on Delivery">Cash on Delivery</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                        </select>
                                    );
                                }

                                // Availability select
                                if (field === "availability") {
                                    return (
                                        <select
                                            key={field}
                                            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.availability ? "border-red-500" : "border-gray-300"}`}
                                            value={newQuotation.availability}
                                            onChange={(e) => setNewQuotation({ ...newQuotation, availability: e.target.value })}
                                        >
                                            <option value="">Select Availability</option>
                                            <option value="Available">Available</option>
                                            <option value="Not Available">Not Available</option>
                                        </select>
                                    );
                                }


                                // Default input
                                return (
                                    <input
                                        key={field}
                                        type="text"
                                        placeholder={placeholdersForModal[field] || placeholdersForModal.default}
                                        className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors[field] ? "border-red-500" : "border-gray-300"}`}
                                        value={newQuotation[field]}
                                        onChange={(e) => setNewQuotation({ ...newQuotation, [field]: e.target.value })}
                                    />
                                );
                            })}
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
                                onClick={() => { setShowModal(false); setErrors({}); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                                onClick={() => handleAdd(newQuotation)}
                            >
                                Add
                            </button>
                        </div>

                        {/* Close icon */}
                        <button
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
                            onClick={() => { setShowModal(false); setErrors({}); }}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

            {/* Staging Table */}
            <StagingTable
                ref={stagingRef}
                visible={showStaging}
                setVisible={setShowStaging}
                apiProp={`${apiBaseUrl}/api/quotations/batch`}
                entryDict={Object.fromEntries(
                    Object.keys(newQuotation).filter((key) => key !== "date" && key !== "total").map((key) => {

                        // Default type and options
                        let type = "input";
                        let options;
                        const label = fieldLabels[key];
                        let additionalTypes = null;

                        // Map frontend keys to backend keys
                        let backendKey = key;
                        if (key === "customerName") backendKey = "customer_name";
                        if (key === "item") backendKey = "item_name";

                        // Set select options for specific fields
                        if (key === "paymentTerm") {
                            backendKey = "payment_term";
                            type = "select";
                            options = ["30 Days PDC", "60 Days PDC", "120 Days PDC", "Cash on Delivery", "Bank Transfer"];
                        }

                        if (key === "availability") {
                            type = "select";
                            options = ["Available", "Not Available"];
                        }

                        if (key == "status") {
                            type = "select";
                            options = ["Pending", "Completed"];
                        }

                        if (key == "total") {
                            additionalTypes = "number";
                        }

                        return [backendKey, { type, options, label, additionalTypes }];
                    })
                )}
            />
            
        </div>
    );
};

export default QuotationsTable;
